/**
 * Strategy orders drawer — a docked, READ-ONLY list of the SELECTED strategies'
 * running positions and pending orders, grouped by strategy. Toggled from the
 * right-rail strategies button and opened with the Strategies tab. Same look as
 * the Signal orders drawer (shared CSS module); data comes from the
 * `strategies` slice, never the user's own `trading` slice. No actions.
 */
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setStrategyOrdersOpen } from "@/state/slices/strategiesSlice";
import { parseSetup } from "./setup";
import { StatusPills } from "./StatusPill";
import styles from "@/components/SignalChart/SignalOrdersPanel.module.css";
import own from "./Strategies.module.css";

const fmtVol = (v: number) => v.toFixed(2);
const fmtMoney = (v: number) => `${v >= 0 ? "" : "-"}$${Math.abs(v).toFixed(2)}`;
const priceDigits = (symbol: string) => (symbol.startsWith("BTC") ? 2 : 3);
const fmtPrice = (v: number, symbol: string) => v.toFixed(priceDigits(symbol));

export function StrategyOrdersPanel() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.strategies.ordersOpen);
  const enabled = useAppSelector((s) => s.strategies.enabled);
  const online = useAppSelector((s) => s.strategies.online);
  const strategies = useAppSelector((s) => s.strategies.strategies);
  const selectedIds = useAppSelector((s) => s.strategies.selectedIds);
  const focusedId = useAppSelector((s) => s.strategies.focusedId);
  const book = useAppSelector((s) => s.strategies.book);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setStrategyOrdersOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  if (!open || !enabled) return null;

  // Focus mode narrows the drawer too, so the list always matches the chart.
  const ids = focusedId ? [focusedId] : selectedIds;
  const selected = ids
    .map((id) => strategies.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => !!s);
  const totalPnl = selected.reduce((sum, s) => sum + s.floating, 0);

  return (
    <aside className={styles.drawer} aria-label="Strategy orders">
      <div className={styles.header}>
        <div className={styles.title}>
          <span
            className={styles.dot}
            style={{ background: online ? "var(--up)" : "var(--amber)" }}
            aria-hidden
          />
          Strategy orders
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close strategy orders"
          onClick={() => dispatch(setStrategyOrdersOpen(false))}
        >
          ×
        </button>
      </div>

      {!online ? (
        <div className={styles.empty}>Strategy runner offline.</div>
      ) : selected.length === 0 ? (
        <div className={styles.empty}>No strategies selected — pick some in the Strategies tab.</div>
      ) : (
        <div className={styles.body}>
          <div className={styles.summary}>
            <div className={styles.summaryCell}>
              <span className={styles.summaryLabel}>{focusedId ? "Focused" : "Selected"}</span>
              <span className={styles.summaryValue}>{selected.length}</span>
            </div>
            <div className={styles.summaryCell}>
              <span className={styles.summaryLabel}>Floating P&amp;L</span>
              <span className={`${styles.summaryValue} ${totalPnl >= 0 ? own.up : own.down}`}>
                {fmtMoney(totalPnl)}
              </span>
            </div>
          </div>

          {selected.map((s) => {
            const positions = book[s.id]?.positions ?? [];
            const orders = book[s.id]?.orders ?? [];
            return (
              <section key={s.id}>
                <div className={styles.sectionHead}>
                  <span className={own.barSw} style={{ background: s.color }} aria-hidden />
                  {s.name}
                  <StatusPills status={s.status} compact />
                  {s.status.running > 0 ? (
                    <span className={s.floating >= 0 ? own.up : own.down}>{fmtMoney(s.floating)}</span>
                  ) : null}
                </div>
                {positions.length === 0 && orders.length === 0 ? (
                  <div className={styles.emptyRow}>
                    {s.status.why || "Flat — waiting for a setup."}
                  </div>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Setup</th>
                        <th className={styles.num}>Vol</th>
                        <th className={styles.num}>Entry</th>
                        <th className={styles.num}>SL</th>
                        <th className={styles.num}>TP</th>
                        <th className={styles.num}>P&amp;L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((p) => {
                        const isBuy = p.type === 0;
                        return (
                          <tr key={`p${p.ticket}`}>
                            <td>
                              <span className={`${styles.side} ${isBuy ? own.up : own.down}`}>
                                {isBuy ? "BUY" : "SELL"}
                              </span>
                            </td>
                            <td>{parseSetup(p.comment)}</td>
                            <td className={styles.num}>{fmtVol(p.volume)}</td>
                            <td className={styles.num}>{fmtPrice(p.price_open, p.symbol)}</td>
                            <td className={styles.num}>{p.sl > 0 ? fmtPrice(p.sl, p.symbol) : "—"}</td>
                            <td className={styles.num}>{p.tp > 0 ? fmtPrice(p.tp, p.symbol) : "—"}</td>
                            <td className={`${styles.num} ${p.profit >= 0 ? own.up : own.down}`}>
                              {fmtMoney(p.profit)}
                            </td>
                          </tr>
                        );
                      })}
                      {orders.map((o) => {
                        const isBuy = o.type_label.toLowerCase().startsWith("buy");
                        return (
                          <tr key={`o${o.ticket}`}>
                            <td>
                              <span className={`${styles.side} ${isBuy ? own.up : own.down}`}>
                                {o.type_label.replace(/_/g, " ").toUpperCase()}
                              </span>
                            </td>
                            <td>{parseSetup(o.comment)}</td>
                            <td className={styles.num}>{fmtVol(o.volume_current)}</td>
                            <td className={styles.num}>{fmtPrice(o.price_open, o.symbol)}</td>
                            <td className={styles.num}>{o.sl > 0 ? fmtPrice(o.sl, o.symbol) : "—"}</td>
                            <td className={styles.num}>{o.tp > 0 ? fmtPrice(o.tp, o.symbol) : "—"}</td>
                            <td className={styles.num}>pending</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            );
          })}
        </div>
      )}
    </aside>
  );
}
